import { useEffect, useState } from 'react';
import { ActionIcon, Button, Modal, Text, Textarea } from 'rizzui';
import { IoClose } from 'react-icons/io5';
import { toast } from 'sonner';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { IncidenceService } from '../../../services/incidence/incidenceService';

interface TutorIncidenceResolveModalProps {
    incidence: any | null;
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
}

const TutorIncidenceResolveModal = ({ incidence, isOpen, onClose, refetch }: TutorIncidenceResolveModalProps) => {
    const { handleErrors } = useHandleErrors();
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleResolve = async (resolutionAction: 'reactivate' | 'discard') => {
        if (!incidence?.id) {
            return;
        }

        setIsLoading(true);

        try {
            const service = new IncidenceService();
            const response = await (await service.resolveTutorIncidence(incidence.id, resolutionAction, comment)).getResponseData();

            if (!response?.success) {
                handleErrors(response);
                return;
            }

            toast.success('Incidencia de tutor resuelta correctamente');
            setComment('');
            onClose();
            refetch();
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) {
            setComment('');
        }
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="sm"
            overlayClassName="backdrop-blur"
            containerClassName="!max-w-xl !shadow-xl"
            className="z-[999] [&_.pointer-events-none]:overflow-visible"
        >
            <div className="m-auto px-7 pt-6 pb-4">
                <div className="mb-6 flex items-center justify-between">
                    <Text className="text-xl">Resolver incidencia de tutor</Text>
                    <ActionIcon size="sm" variant="text" onClick={onClose}>
                        <IoClose className="h-auto w-6" strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                <div className="space-y-2">
                    <Text className="text-sm font-medium text-gray-900">{incidence?.code || 'Incidencia'}</Text>
                    <Text className="text-sm text-gray-600">{incidence?.subject || 'Sin asunto'}</Text>
                </div>

                <Textarea
                    className="mt-5"
                    label="Comentario"
                    rows={5}
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                />
            </div>

            <div className="flex flex-wrap justify-center gap-3 p-7 pt-0">
                <Button
                    color="primary"
                    disabled={isLoading}
                    onClick={() => handleResolve('reactivate')}
                >
                    Reactivar alumno
                </Button>
                <Button
                    variant="outline"
                    color="danger"
                    disabled={isLoading}
                    onClick={() => handleResolve('discard')}
                >
                    Descartar alumno
                </Button>
            </div>
        </Modal>
    );
};

export default TutorIncidenceResolveModal;
